import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Check, Phone, ChevronRight } from 'lucide-react';
import { useToast } from '../Toast';
import api from '../../lib/api';
import type { TeamAlert } from '../../hooks/use-team-overview';

interface AlertCardProps {
  alert: TeamAlert;
  phone?: string;
  onReviewed?: () => void;
}

const severityStyles: Record<string, string> = {
  CRITICAL: 'bg-red-50 border-red-200',
  HIGH: 'bg-amber-50 border-amber-200',
  MEDIUM: 'bg-gray-50 border-gray-200',
};

const severityBadge: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700',
  HIGH: 'bg-amber-100 text-amber-700',
  MEDIUM: 'bg-gray-100 text-gray-600',
};

const severityLabel: Record<string, string> = {
  CRITICAL: 'Crítico',
  HIGH: 'Alto',
  MEDIUM: 'Médio',
};

export default function AlertCard({ alert, phone, onReviewed }: AlertCardProps) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isClientSide = alert.id.startsWith('client-');

  const isRealAlert = !alert.id.startsWith('client-');

  const reviewMutation = useMutation({
    mutationFn: async () => {
      if (!isRealAlert) return;
      await api.patch(`/fraud-alerts/${alert.id}/review`, { status: 'REVIEWED' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supervisor', 'team-overview'] });
      onReviewed?.();
      toast({ title: 'Alerta marcado como visto', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao marcar alerta', variant: 'error' });
    },
  });

  const cardStyle = severityStyles[alert.severity] ?? severityStyles.MEDIUM;
  const badgeStyle = severityBadge[alert.severity] ?? severityBadge.MEDIUM;
  const label = severityLabel[alert.severity] ?? alert.severity;

  const timeStr = new Date(alert.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className={`rounded-xl border p-3 ${cardStyle}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badgeStyle}`}>
          {label}
        </span>
        <span className="text-[10px] text-gray-400">{timeStr}</span>
      </div>

      <p className="text-xs font-medium text-gray-900 mb-0.5">{alert.message}</p>
      <p className="text-[10px] text-gray-500 mb-3">{alert.promoter_name}</p>

      <div className="flex items-center gap-2">
        {!isClientSide && alert.status === 'OPEN' && (
          <button
            onClick={() => reviewMutation.mutate()}
            disabled={reviewMutation.isPending}
            className="flex items-center gap-1 text-[11px] font-medium text-white bg-green-600 active:bg-green-700 disabled:opacity-50 px-2.5 py-1 rounded-lg transition-colors"
          >
            <Check size={12} />
            Visto
          </button>
        )}

        {phone && (
          <a
            href={`tel:${phone}`}
            className="flex items-center gap-1 text-[11px] font-medium text-blue-600 bg-blue-50 active:bg-blue-100 px-2.5 py-1 rounded-lg transition-colors"
          >
            <Phone size={12} />
            Ligar
          </a>
        )}

        <button
          onClick={() => setLocation(`/supervisor/team/${alert.promoter_id}`)}
          className="flex items-center gap-1 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 active:bg-gray-50 px-2.5 py-1 rounded-lg transition-colors ml-auto"
        >
          Ver detalhes
          <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}
